import clsx from 'clsx'
import { useRouter } from 'next/router'
import { Form, Formik, FormikHelpers } from 'formik'
import { useToast } from '@/hooks/useToast'

/* blocks */
import Link from '@/atoms/Link/Link'
import Button from '@/atoms/Button/Button'
import TextField from '@/molecules/FormikFields/TextField'

/* utils */
import { FormValues, initialFormValues, validationSchema } from './schema'
import { dataLayerFormSubmit } from '@/utils/functions/dataLayerEvents'

/* styling */
import styles from './_.module.css'

interface Props {
  isAside: boolean
}

function BlogNewsletterForm({ isAside }: Props): JSX.Element {
  const router = useRouter()
  const { toast, showToast } = useToast()

  const handleSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    try {
      const result = await submitToMailChimp(
        `/api/forms/blog-newsletter/`,
        values
      )

      if (!result.ok) {
        showToast({
          title: result.data.message,
          type: 'error',
          swipeDirection: 'up'
        })
        return
      }
      dataLayerFormSubmit({
        formName: 'Newsletter Form',
        formValues: values
      })
      helpers.resetForm()
      router.push({
        pathname: `/thank-you/`,
        query: { param: 'blog-newsletter' }
      })
    } catch (_e) {
      showToast({
        title: 'Please try again',
        type: 'error',
        swipeDirection: 'up'
      })
    }
  }

  const submitToMailChimp = async (url: string, values: FormValues) => {
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        company: values.company,
        sourceUrl: router.asPath,
        status: 'subscribed'
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })

    const data = await response.json()
    return { ok: response.ok, data }
  }

  return (
    <div className="font-body">
      <Formik<FormValues>
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form name="Blog Newsletter Form" id="blog-newsletter-form">
            <div className={clsx(!isAside ? 'md:flex gap-5' : '')}>
              <TextField
                name="firstName"
                className={clsx('mb-6', styles.formField)}
                placeholder="First Name"
                required
              />
              <TextField
                name="lastName"
                className={clsx('mb-6', styles.formField)}
                placeholder="Last Name"
                required
              />
            </div>
            <TextField
              name="company"
              className={clsx('mb-6', styles.formField)}
              placeholder="Company Name"
              required
            />
            <TextField
              name="email"
              className={clsx('mb-7', styles.formField)}
              placeholder="example@peppercontent.in"
              required
            />
            <div>
              <Button
                className={clsx(
                  'text-xl font-medium | rounded! | w-full mb-6',
                  styles.registerBtn
                )}
                size="large"
                htmlType="submit"
              >
                {isSubmitting ? 'Loading...' : 'Subscribe to Newsletter'}
              </Button>
            </div>
            <div className="text-caption md:text-small text-center text-white | max-w-col-3 md:max-w-col-5 mx-auto">
              By signing up you agree to our
              <br />
              <Link
                href="/terms/#section-1"
                target="_blank"
                className="font-semibold underline underline-offset-4 mx-1"
              >
                Terms of Service
              </Link>
              and
              <Link
                href="/privacy/#section-1"
                target="_blank"
                className="font-semibold underline underline-offset-4 mx-1"
              >
                Privacy Policy
              </Link>
            </div>
          </Form>
        )}
      </Formik>
      {toast}
    </div>
  )
}

export default BlogNewsletterForm
